import { useCallback, useMemo } from "react";
import { useLocation, useMatch } from "react-router-dom";

import { useFlowDirty } from "../contexts/FlowDirtyContext";
import { useBrowserRouterBlocker } from "../hooks/useBrowserRouterBlocker";

function toHref(loc: { pathname: string; search?: string; hash?: string }) {
  const pathname = loc?.pathname ?? "";
  const search = loc?.search ?? "";
  const hash = loc?.hash ?? "";
  return `${pathname}${search}${hash}`;
}

export function FlowUnsavedChangesGuard() {
  const location = useLocation();
  const editorMatch = useMatch("/my-quant-flows/editor/:workflowId");
  const { isDirtyForWorkflow } = useFlowDirty();

  const workflowId = editorMatch?.params?.workflowId;

  const shouldGuard = useMemo(() => {
    if (!workflowId) return false;
    return isDirtyForWorkflow(workflowId);
  }, [workflowId, isDirtyForWorkflow]);

  const blocker = useCallback(
    (tx: { location: { pathname: string; search: string; hash: string }; retry: () => void }) => {
      const nextHref = toHref(tx.location);
      const currentHref = toHref(location);

      // Avoid doing anything on no-op transitions.
      if (nextHref === currentHref) {
        tx.retry();
        return;
      }

      // Force a real document navigation so the existing `useBeforeUnload` hook
      // can show the browser-native "unsaved changes" prompt.
      window.location.assign(nextHref);
    },
    [location],
  );

  useBrowserRouterBlocker(blocker, shouldGuard);

  return null;
}
